import tailwindClassnames from "../common/TailwindClassnames";
import { t } from "i18next";
import TextInput from "../common/TextInput";
import DateRangeSelector from "../common/DateRangeSelector";
import { useState } from "react";
import { SensorSearchQueryType } from "../common/QueryTypes";
import { formatDateAsLocalString } from "../common/helpers";

type SearchBarSensorsProps = {
  canAddSensor: boolean;
  onAddSensor: Function;
  onChangeSearchQuery: any;
  onSearch: Function;
  sensorSearchQuery: SensorSearchQueryType;
  /**
   * Opens the CSV import (AKL-813). Supplied by the parent only when the user may create
   * sensors — the same gate as "+ Add Sensor" — so the button renders on the prop alone.
   */
  onImportFromCsv?: () => void;
};

const SearchBarSensors = ({
  canAddSensor,
  onAddSensor,
  onChangeSearchQuery,
  onSearch,
  sensorSearchQuery,
  onImportFromCsv,
}: SearchBarSensorsProps) => {
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();

  const onDateChange = (dates: any) => {
    let [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    // null (not "") when a bound is unset, so the search omits it entirely
    // instead of sending a literal `purchase_date_end=` — otherwise a start
    // date with no end date wouldn't return sensors purchased after it,
    // since the empty end bound doesn't parse as "no upper bound".
    onChangeSearchQuery({
      target: {
        name: "purchase_date_start",
        value: start ? formatDateAsLocalString(start) : null,
      },
    });
    onChangeSearchQuery({
      target: {
        name: "purchase_date_end",
        value: end ? formatDateAsLocalString(end) : null,
      },
    });
  };

  const handleCheckboxChange = (e: any) => {
    onChangeSearchQuery({
      target: {
        name: e.target.name,
        value: !e.target.checked,
      },
    });
  };

  return (
    <div className={tailwindClassnames.tableCardToolbar}>
      <TextInput
        name={"sensor_name"}
        label={t("Name")}
        placeholder={t("Search sensor name")}
        value={sensorSearchQuery.sensor_name || ""}
        error={""}
        disabled={false}
        className={tailwindClassnames.tableFilterInput}
        type={"text"}
        initialFocus={true}
        onChange={(e: any) => onChangeSearchQuery(e)}
        onKeyPress={(e) => {
          if (e.key === "Enter") {
            onSearch();
          }
        }}
      />
      <TextInput
        name={"model"}
        label={t("Model")}
        placeholder={t("Search sensor model")}
        value={sensorSearchQuery.model || ""}
        error={""}
        disabled={false}
        className={tailwindClassnames.tableFilterInput}
        type={"text"}
        initialFocus={false}
        onChange={(e: any) => onChangeSearchQuery(e)}
        onKeyPress={(e) => {
          if (e.key === "Enter") {
            onSearch();
          }
        }}
      />
      <TextInput
        name={"serial_number"}
        label={t("Serial Number")}
        placeholder={t("Search serial number")}
        value={sensorSearchQuery.serial_number || ""}
        error={""}
        disabled={false}
        className={tailwindClassnames.tableFilterInput}
        type={"text"}
        initialFocus={false}
        onChange={(e: any) => onChangeSearchQuery(e)}
        onKeyPress={(e) => {
          if (e.key === "Enter") {
            onSearch();
          }
        }}
      />
      <div className="z-10">
        <DateRangeSelector
          label={`${t("Purchase Date Range")}`}
          startDate={startDate}
          endDate={endDate}
          onDateChange={(e: any) => onDateChange(e)}
          onKeyDown={(e: any) => {
            if (e.key === "Enter") {
              onSearch();
            }
          }}
        />
      </div>
      <div className="flex flex-row items-center pb-2">
        <input
          className={tailwindClassnames.checkBox}
          checked={!sensorSearchQuery.active_only}
          type="checkbox"
          name="active_only"
          onChange={(e: any) => handleCheckboxChange(e)}
        />
        <label className={tailwindClassnames.checkBoxLabel}>
          {t("Decommissioned Sensors")}
        </label>
      </div>
      <button
        className={tailwindClassnames.btnFilterApply}
        onClick={() => onSearch()}
      >
        {t("Apply filters")}
      </button>
      <div className="flex-1" />
      {onImportFromCsv && (
        <button
          type="button"
          onClick={onImportFromCsv}
          className={tailwindClassnames.btnSecondary}
          data-testid="import-sensors-from-csv-toolbar"
        >
          {t("Import from CSV")}
        </button>
      )}
      {/* Conditional rendering, not a "hidden" class swap, so the gate
          can't be defeated by display utilities. */}
      {canAddSensor && (
        <button
          onClick={() => onAddSensor()}
          className={tailwindClassnames.btnPrimary}
        >
          <span aria-hidden="true">+ </span>
          {t("Add Sensor")}
        </button>
      )}
    </div>
  );
};

export default SearchBarSensors;
